// frontend/src/services/UserService.ts
import axios from 'axios';
import showErrorToast from '../components/Toasts/ShowErrorToast';
import { User } from '../models/User';
import { AuthService } from './authService';

const API_URL = process.env.REACT_APP_SERVER_URL + "/users";

// Create axios instance with interceptors
const apiClient = axios.create({
    baseURL: API_URL,
    timeout: 10000,
});

// Request interceptor to add token
apiClient.interceptors.request.use(
    (config) => {
        const token = AuthService.getToken();
        if (token) {
            config.headers.Authorization = `Bearer ${token}`;
        }
        return config;
    },
    (error) => {
        return Promise.reject(error);
    }
);

// Response interceptor to handle token expiration
apiClient.interceptors.response.use(
    (response) => {
        return response;
    },
    async (error) => {
        const originalRequest = error.config;

        // If token is expired and we haven't already tried to refresh
        if (error.response?.status === 401 && !originalRequest._retry) {
            originalRequest._retry = true;

            // Clear expired token
            AuthService.clearAuth();

            // Emit custom event for token expiration
            window.dispatchEvent(new CustomEvent('tokenExpired'));

            return Promise.reject(error);
        }

        return Promise.reject(error);
    }
);

export const UserService = {

    login: async (username: string, password: string) => {
        try {
            const response = await axios.post(`${API_URL}/login`, {
                username,
                password,
            });
            // Save token and role in localStorage using AuthService
            if (response.data.gpcwstoken_kw) {
                AuthService.storeToken(response.data.gpcwstoken_kw, 10 * 60 * 60 * 1000); // 10 hours
                localStorage.setItem('gpcwstoken_kw_role', response.data.gpcwstoken_kw);
            }
            if (!response.data.success) {
                showErrorToast(response.data.message);
            }
            return response.data.success;
        } catch (error) {
            console.error('Login failed:', error);
            showErrorToast('Login Error: ' + error);
            throw error;
        }
    },

    getProfile: async () => {
        try {
            const response = await apiClient.get('/profile');

            // Check if the response indicates an expired or invalid token
            if (!response.data.success) {
                showErrorToast('Token expired or invalid: ' + response.data.message);
                localStorage.removeItem('gpcwstoken_kw');
                localStorage.removeItem('gratefulmsrole');
                return null; // Explicitly return null if token is expired
            }

            return response.data.userProfile;
        } catch (error) {
            console.error('Failed to fetch profile:', error);
            showErrorToast('Error fetching user profile: ' + error);
            localStorage.removeItem('gpcwstoken_kw');
            localStorage.removeItem('gratefulmsrole');
            throw error; // Re-throw error to propagate it to the calling component
        }
    },

    logout: () => {
        AuthService.clearAuth();
    },

    // Get all users method
    getAllUsers: async () => {
        try {
            const response = await apiClient.get('/getAllUsers');
            if (!response.data.success) {
                showErrorToast(response.data.message);
            }
            return response.data.users;
        } catch (error) {
            console.error('Failed to fetch users:', error);
            showErrorToast('Fetch Users Error: ' + error);
            throw error;
        }
    },

    getAllUsersByWarehouseId: async (warehouseId: string) => {
        try {
            const response = await apiClient.get(`/getAllUsersByWarehouseId/${warehouseId}`);
            if (!response.data.success) {
                showErrorToast(response.data.message);
            }
            return response.data.users;
        } catch (error) {
            console.error('Failed to fetch users:', error);
            showErrorToast('Fetch Users Error: ' + error);
            throw error;
        }
    },

    getUserById: async (id: string): Promise<User | null> => {
        try {
            const response = await apiClient.get(`/getUserById/${id}`);
            if (!response.data.success) {
                showErrorToast(response.data.message);
            }
            return response.data.user;
        } catch (error) {
            console.error('Failed to fetch user:', error);
            showErrorToast('Fetch User Error: ' + error);
            throw error;
        }
    },

    addUser: async (userData: Record<string, any>) => {
        try {
            const response = await apiClient.post('/add', userData);
            return response.data;
        } catch (error) {
            console.error('Failed to add user:', error);
            showErrorToast('Add User Error: ' + error);
            throw error;
        }
    },

    updateUser: async (id: string, userData: Partial<User>) => {
        try {
            const response = await apiClient.put(`/update/${id}`, userData, {
                headers: {
                    'Content-Type': 'application/json',
                },
            });

            return response.data;
        } catch (error) {
            console.error('Failed to update user:', error);
            showErrorToast('Update User Error: ' + error);
            throw error;
        }
    },

    deleteUser: async (id: string) => {
        try {
            const response = await apiClient.delete(`/delete/${id}`);
        } catch (error) {
            console.error('Failed to delete user:', error);
            showErrorToast('Delete User Error: ' + error);
            throw error;
        }
    },

    resetPassword: async (userId: number, currentPassword: string, newPassword: string) => {
        try {
            const response = await apiClient.post('/resetpassword', { userId, currentPassword, newPassword });
            return response.data;
        } catch (error) {
            console.error('Error resetting password:', error);
            throw new Error('Failed to reset password');
        }
    },

    resetPasswordByRandom: async (username: string) => {
        try {
            const response = await apiClient.post('/resetPasswordByRandom', { username });
            return response.data;
        } catch (error) {
            console.error('Error resetting password by random:', error);
            throw error;
        }
    },

    setPasswordDirect: async (userId: number | bigint, newPassword: string, sendEmail: boolean = false) => {
        try {
            const response = await apiClient.post('/setPasswordDirect', { userId: Number(userId), newPassword, sendEmail });
            return response.data;
        } catch (error) {
            console.error('Error setting password directly:', error);
            throw error;
        }
    }

};
